import { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import MobileSubTitle from "../../../components/mobile/MobileSubTitle";
import { getSetting, getSettings, updateSetting, sendTestEmail } from "../../../api/settings";
import VisibilityIcon from "../../../components/icons/VisibilityIcon";
import VisibilityOffIcon from "../../../components/icons/VisibilityOffIcon";
import SendIcon from "../../../components/icons/SendIcon";

const SubTitleContainer = styled.div`
  padding: 60px 16px 0;
  display: flex;
  align-items: flex-end;
  gap: 10px;
  color: #2aa4d5;
  font-weight: 700;

  h3 {
    font-size: 18px;
  }

  span {
    font-size: 20px;
  }

  ::before {
    position: absolute;
    left: 0px;
    bottom: calc(100% + 15px);
    content: "";
    display: block;
    width: 25px;
    height: 3px;
    background-color: #186fb2;
  }
`;

const Container = styled.div`
  padding: 0 16px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 100px;
`;


const SettingForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 25px;
`;

const SettingItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #333;
  
  span {
    color: #ff6666;
    margin-left: 5px;
  }
`;

const Input = styled.input`
  width: 100%;
  height: 40px;
  border-radius: 10px;
  border: 1px solid var(--border-c);
  padding: 0 12px;
  font-size: 13px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: var(--primary-c);
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 80px;
  border-radius: 10px;
  border: 1px solid var(--border-c);
  padding: 12px;
  font-size: 13px;
  box-sizing: border-box;
  resize: vertical;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: var(--primary-c);
  }
`;

const HelpText = styled.p`
  font-size: 11px;
  color: #666;
  margin: 0;
  line-height: 1.4;
`;

const PasswordContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  border-radius: 10px;
  border: 1px solid var(--border-c);
  padding: 0 12px;
  box-sizing: border-box;

  > input {
    width: 100%;
    height: 40px;
    border: none;
    outline: none;
    font-size: 13px;
    box-sizing: border-box;
  }

  > button {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    border: none;
    background-color: transparent;
    padding: 0;
    margin: 0;
    border-radius: 50%;
    margin-right: -10px;
    cursor: pointer;

    > svg {
      width: 20px;
      height: 20px;
    }
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 10px;
`;

const Button = styled.button`
  width: 100px;
  height: 40px;
  border-radius: 10px;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all 0.3s ease;
  background-color: var(--primary-c);

  &:hover:not(:disabled) {
    background-color: var(--primary-c-hover);
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
  }

  &:disabled {
    background-color: #e0e0e0;
    color: #9e9e9e;
    cursor: not-allowed;
  }
`;

const SaveAllButton = styled(Button)`
  width: 120px;
  background-color: #2aa4d5;
  
  &:hover:not(:disabled) {
    background-color: #0089b3;
  }
`;

const TestEmailButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  max-width: 160px;
  padding: 0 10px;
  height: 40px;
  border-radius: 10px;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all 0.3s ease;
  background-color: var(--primary-c);

  &:hover:not(:disabled) {
    background-color: var(--primary-c-hover);
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
  }

  &:disabled {
    background-color: #e0e0e0;
    color: #9e9e9e;
    cursor: not-allowed;
  }
`;

const Spinner = styled.div`
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const Message = styled.div`
  padding: 10px 12px;
  border-radius: 10px;
  margin-top: 10px;
  font-size: 13px;
  ${props => props.type === 'success'
    ? 'background-color: #d4edda; color: #155724; border: 1px solid #c3e6cb;'
    : 'background-color: #f8d7da; color: #721c24; border: 1px solid #f5c6cb;'
  }
`;

export const Setting = () => {
  const [managerEmails, setManagerEmails] = useState("");
  const [senderEmail, setSenderEmail] = useState("");
  const [senderPassword, setSenderPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [isEditingSenderPassword, setIsEditingSenderPassword] = useState(false);
  const [showSenderPassword, setShowSenderPassword] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      const data = await getSettings();
      const settings = data.settings || [];

      const managerEmailsSetting = settings.find(s => s.key === "MANAGER_EMAILS");
      const senderEmailSetting = settings.find(s => s.key === "SENDER_EMAIL");
      const senderPasswordSetting = settings.find(s => s.key === "SENDER_PASSWORD");

      setManagerEmails(managerEmailsSetting?.value || "");
      setSenderEmail(senderEmailSetting?.value || "");
      setSenderPassword(senderPasswordSetting?.value || "");
    } catch (error) {
      console.error("설정 불러오기 실패:", error);
      setMessage({ type: "error", text: "설정을 불러오는데 실패했습니다." });
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSaveAll = async () => {
    setIsLoading(true);
    setMessage(null);

    try {
      await Promise.all([
        updateSetting("MANAGER_EMAILS", managerEmails.trim()),
        updateSetting("SENDER_EMAIL", senderEmail.trim()),
        updateSetting("SENDER_PASSWORD", senderPassword.trim()),
      ]);

      setMessage({ type: "success", text: "모든 설정이 저장되었습니다." });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error("설정 저장 실패:", error);
      setMessage({ type: "error", text: "설정 저장에 실패했습니다." });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async (key, value) => {
    setIsLoading(true);
    setMessage(null);

    try {
      await updateSetting(key, value.trim());
      setMessage({ type: "success", text: "설정이 저장되었습니다." });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error("설정 저장 실패:", error);
      setMessage({ type: "error", text: "설정 저장에 실패했습니다." });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendTestEmail = async () => {
    setIsLoading(true);
    setMessage(null);

    const managerEmailsSetting = await getSettings();
    const managerEmails = managerEmailsSetting.settings.find(s => s.key === "MANAGER_EMAILS");

    try {
      await sendTestEmail();
      alert(`${managerEmails.value}로 테스트 이메일을 전송했습니다.`);
    } catch (error) {
      console.error("테스트 이메일 전송 실패:", error);
      alert("테스트 이메일 전송에 실패했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <SubTitleContainer>
        <MobileSubTitle title="시스템 설정" />
      </SubTitleContainer>
      <Container>
        <SettingForm>
          <SettingItem>
            <Label htmlFor="managerEmails">
              관리자 이메일<span>*</span>
            </Label>
            <Textarea
              id="managerEmails"
              value={managerEmails}
              onChange={(e) => setManagerEmails(e.target.value)}
              placeholder="여러 이메일은 쉼표(,)로 구분해주세요"
            />
            <HelpText>상담 신청 알림을 받을 관리자 이메일 주소를 입력하세요. 여러 개는 쉼표로 구분합니다.</HelpText>
            <Button
              onClick={() => handleSave("MANAGER_EMAILS", managerEmails)}
              disabled={isLoading || !managerEmails.trim()}
            >
              저장
            </Button>
          </SettingItem>

          <SettingItem>
            <Label htmlFor="senderEmail">
              발신용 이메일<span>*</span>
            </Label>
            <Input
              id="senderEmail"
              type="email"
              value={senderEmail}
              onChange={(e) => setSenderEmail(e.target.value)}
              placeholder="발신용 이메일 주소를 입력하세요"
            />
            <HelpText>상담 신청 알림을 보낼 이메일 주소를 입력하세요.</HelpText>
            <Button
              onClick={() => handleSave("SENDER_EMAIL", senderEmail)}
              disabled={isLoading || !senderEmail.trim()}
            >
              저장
            </Button>
          </SettingItem>

          <SettingItem>
            <Label htmlFor="senderPassword">
              발신용 이메일 비밀번호<span>*</span>
            </Label>
            <PasswordContainer>
              <input
                id="senderPassword"
                type={showSenderPassword ? "text" : "password"}
                value={senderPassword}
                onChange={(e) => {
                  if (!isEditingSenderPassword) {
                    setSenderPassword("");
                    setIsEditingSenderPassword(true);
                  } else {
                    setSenderPassword(e.target.value);
                  }
                }}
                placeholder="발신용 이메일 비밀번호를 입력하세요"
              />
              <button onClick={() => setShowSenderPassword(!showSenderPassword)}>
                {showSenderPassword ? (
                  <VisibilityOffIcon fill="#186fb2" />
                ) : (
                  <VisibilityIcon />
                )}
              </button>
            </PasswordContainer>
            <HelpText>발신용 이메일 계정의 비밀번호를 입력하세요.</HelpText>
            <Button
              onClick={() => {
                handleSave("SENDER_PASSWORD", senderPassword);
                setIsEditingSenderPassword(false);
                setShowSenderPassword(false);
              }}
              disabled={isLoading || !isEditingSenderPassword}
            >
              저장
            </Button>
          </SettingItem>

          <SettingItem>
            <TestEmailButton onClick={handleSendTestEmail} disabled={isLoading}>
              {isLoading ? (
                <>
                  <Spinner /> 전송 중...
                </>
              ) : (
                <>
                  <SendIcon fill="#FFFFFF" /> 테스트 이메일 전송
                </>
              )}
            </TestEmailButton>
          </SettingItem>

          {/* <ButtonContainer>
            <SaveAllButton onClick={handleSaveAll} disabled={isLoading}>
              전체 저장
            </SaveAllButton>
          </ButtonContainer> */}

          {message && (
            <Message type={message.type}>{message.text}</Message>
          )}
        </SettingForm>
      </Container>
    </>
  );
};

